import type { PropsWithChildren } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { styled, useProps } from '@tamagui/core';
import type { GetProps } from '@tamagui/core';
import { Dialog as TamaguiDialog } from '@tamagui/dialog';
import { ScrollView } from 'react-native';

import { Box } from '../Box';
import { VisuallyHidden } from '../shared/VisuallyHidden';
import { useBricksDialogContext } from './context';

const { dialog: dialogTokens } = tokens;

// <TamaguiDialog.Description asChild><Text /></TamaguiDialog.Description>
// wasn't working, so using styled(TamaguiDialog.Description) instead
const StyledTitle = styled(TamaguiDialog.Title, {
  name: 'DialogTitle',
  fontFamily: '$body',
  color: dialogTokens.color.content.default,
  variants: {
    typography: {
      '...fontSize': (fontSize) => ({
        fontSize,
        fontWeight: fontSize,
        letterSpacing: fontSize,
        lineHeight: fontSize,
      }),
    },
  } as const,
});

type TitleProps = GetProps<typeof StyledTitle>;
type PublicTitleProps = Omit<TitleProps, 'id' | 'scope'> & {
  id?: never;
  scope?: never;
};

const StyledDescription = styled(TamaguiDialog.Description, {
  name: 'DialogDescription',
  fontFamily: '$body',
  color: dialogTokens.color.content.default,
  variants: {
    typography: {
      '...fontSize': (fontSize) => ({
        fontSize,
        fontWeight: fontSize,
        letterSpacing: fontSize,
        lineHeight: fontSize,
      }),
    },
  } as const,
});

type DescriptionProps = GetProps<typeof StyledDescription>;
type PublicDescriptionProps = Omit<DescriptionProps, 'id' | 'scope'> & {
  id?: never;
  scope?: never;
};

// Had to add these wrappers to get variants working inside of media query props
// like $largerThanSM. Not sure why - possibly a tamagui bug
const Title = (propsIn: TitleProps) => {
  const props = useProps(propsIn);
  return <StyledTitle {...props} />;
};

const Description = (propsIn: DescriptionProps) => {
  const props = useProps(propsIn);
  return <StyledDescription {...props} />;
};

export type DialogContentProps = {
  /**
   * The title of the dialog. Required for a11y. By default, it will render
   * as an "h2" with `typography` set to "$subheaderLarge" on small screens
   * and "$headerMedium" on larger screens. If you need to customize the
   * appearance of the title, pass `titleProps`. If you need to hide the title,
   * pass `hideTitle`.
   */
  title: string;
  /**
   * Optional props to customize the title
   */
  titleProps?: Partial<PublicTitleProps>;
  /**
   * Optional prop to hide the title visually (it will still be rendered to the
   * DOM for a11y)
   */
  hideTitle?: boolean;
  /**
   * The description for the dialog. Required for a11y. By default, it will
   * render as a "p" with `typography` set to "$bodySmall" on small screens and
   * "$bodyMedium" on larger screens. If you need to customize the appearance of
   * the description, pass `descriptionProps`. If you need to hide the
   * description, pass `hideDescription`.
   */
  description: string;
  /**
   * Optional props to customize the description
   */
  descriptionProps?: Partial<PublicDescriptionProps>;
  /**
   * Optional prop to hide the description visually (it will still be rendered
   * to the DOM for a11y)
   */
  hideDescription?: boolean;
};

/**
 * Scrollable content to render inside the Dialog component.
 */
export const Content = ({
  title,
  titleProps,
  hideTitle,
  description,
  descriptionProps,
  hideDescription,
  children,
}: PropsWithChildren<DialogContentProps>) => {
  const dialogContext = useBricksDialogContext();
  const dialogScope = dialogContext?.scope;
  const { id: _titleId, scope: _titleScope, ...safeTitleProps } = titleProps ?? {};
  const {
    id: _descriptionId,
    scope: _descriptionScope,
    ...safeDescriptionProps
  } = descriptionProps ?? {};

  return (
    <Box
      flexGrow={1}
      flexShrink={1}
      paddingTop={dialogTokens.margin.mobile.top}
      paddingBottom={dialogTokens.margin.mobile.bottom}
      $largerThanSM={{
        paddingTop: dialogTokens.margin.desktop.top,
        paddingBottom: dialogTokens.margin.desktop.bottom,
      }}
    >
      <VisuallyHidden visible={!hideTitle}>
        <Box
          paddingHorizontal={dialogTokens.margin.mobile.leftRight}
          paddingBottom={dialogTokens.padding.titleAndDescription.mobile}
          $largerThanSM={{
            paddingHorizontal: dialogTokens.margin.desktop.leftRight,
            paddingBottom: dialogTokens.padding.titleAndDescription.desktop,
          }}
        >
          <Title
            typography={dialogTokens.typography.title.small}
            {...safeTitleProps}
            $largerThanSM={{
              typography: dialogTokens.typography.title.medium,
              marginRight: dialogTokens.margin.desktop.titleRight,
              ...safeTitleProps.$largerThanSM,
            }}
            scope={dialogScope}
          >
            {title}
          </Title>
        </Box>
      </VisuallyHidden>

      <ScrollView>
        <Box
          paddingHorizontal={dialogTokens.margin.mobile.leftRight}
          $largerThanSM={{
            paddingHorizontal: dialogTokens.margin.desktop.leftRight,
          }}
        >
          <VisuallyHidden visible={!hideDescription}>
            <Description
              typography={dialogTokens.typography.description.small}
              {...safeDescriptionProps}
              $largerThanSM={{
                typography: dialogTokens.typography.description.medium,
                ...safeDescriptionProps.$largerThanSM,
              }}
              scope={dialogScope}
            >
              {description}
            </Description>
          </VisuallyHidden>

          <Box>{children}</Box>
        </Box>
      </ScrollView>
    </Box>
  );
};
